/**
 * Convert a date to a timestamp
 * @param date
 */
export const dateToTs = (date: Date): number => Math.floor(date.getTime() / 1000);

/**
 * Move the date to the Monday of its local calendar week, preserving the
 * wall-clock time. Mutates the date.
 * @param date
 */
export const dateStartOfWeek = (date: Date): Date => {
    date.setDate(date.getDate() - dateToDayOfWeekNum(date));
    return date;
}
/**
 * Move the date to the Sunday of its local calendar week, preserving the
 * wall-clock time. Mutates the date.
 * @param date
 */
export const dateEndOfWeek = (date: Date): Date => {
    date.setDate(date.getDate() + (6 - dateToDayOfWeekNum(date)));
    return date;
}

/**
 * Convert a timestamp to a date
 * @param timestamp
 */
export const tsToDate = (timestamp: number) => new Date(timestamp * 1000);

/**
 * new Date object set to 00:00 today
 */
export const todayDate = (): Date => {
    const workingDate = new Date();
    workingDate.setHours(0, 0, 0, 0);
    return workingDate
}

/**
 * Timestamp of today set to 00:00 today
 */
export const todayTs = (): number => dateToTs(todayDate())

/**
 * The API's day-of-week number (0=Monday … 6=Sunday) of the date's local
 * calendar day. Derived from the date's own calendar fields — never from
 * reinterpreting the epoch in another timezone, which shifts the day for
 * clients whose UTC offset differs from the server's.
 * @param date
 */
export const dateToDayOfWeekNum = (date: Date): number => (date.getDay() + 6) % 7;

/**
 * The API's day-of-week number (0=Monday … 6=Sunday) for a timestamp taken at
 * local midnight of a local calendar date (e.g. the slots page's date chips).
 * @param timestamp
 */
export const tsToDayOfWeekNum = (timestamp: number): number => dateToDayOfWeekNum(tsToDate(timestamp));

/**
 * A timestamp the API resolves to the date's local calendar day: noon UTC of
 * that day. The API does its start-of-day/start-of-week maths in the school's
 * timezone; noon UTC falls on the same calendar date in every timezone within
 * UTC±12, so the resolved date can't slip into an adjacent day for any client
 * offset. Use this for every date the app SENDS to the API.
 * @param date
 */
export const dateToServerDateTs = (date: Date): number =>
    Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12) / 1000);

/**
 * The calendar date a server date-timestamp refers to, as a local-midnight
 * Date. The API encodes dates as midnight in the school's timezone; rendering
 * such an epoch with local Date methods shifts it a day for clients west of
 * the school. Reading the UTC fields at noon (epoch + 12h) recovers the
 * intended calendar date for any encoding timezone within UTC±12. Use this for
 * every date-timestamp the app RECEIVES from the API.
 * @param timestamp
 */
export const tsToServerDate = (timestamp: number): Date => {
    const noon = new Date((timestamp + 12 * 3600) * 1000);
    return new Date(noon.getUTCFullYear(), noon.getUTCMonth(), noon.getUTCDate());
};

/**
 * dateToFormat('yyyy-mm-dd hh:ii:ss');
 * @param date
 * @param format
 */
export const dateToFormat = (date: Date, format: string = 'yyyy-mm-dd'): string => {
    const yyyy = date.getFullYear().toString();
    format = format.replace(/yyyy/g, yyyy)
    const yy = date.getFullYear().toString().slice(2,4);
    format = format.replace(/yy/g, yy)
    const mmm = date.toLocaleString('default', {month: 'short'});
    format = format.replace(/mmm/g, mmm);
    const mm = (date.getMonth()+1).toString();
    format = format.replace(/mm/g, (mm[1]?mm:"0"+mm[0]));
    const ddd = date.toLocaleString('default', {weekday: 'short'})
    format = format.replace(/ddd/g, ddd);
    const DD = date.toLocaleString('default', {weekday: 'narrow'})
    format = format.replace(/DD/g, DD);
    const dd  = date.getDate().toString();
    format = format.replace(/dd/g, (dd[1]?dd:"0"+dd[0]));
    const hh = date.getHours().toString();
    format = format.replace(/hh/g, (hh[1]?hh:"0"+hh[0]));
    const ii = date.getMinutes().toString();
    format = format.replace(/ii/g, (ii[1]?ii:"0"+ii[0]));
    const ss = date.getSeconds().toString();
    format = format.replace(/ss/g, (ss[1]?ss:"0"+ss[0]));

    return format;
};

export class Ages {
    date: Date = new Date();

    constructor(dateOrTs?: Date|number) {
        this.date = typeof dateOrTs === "number" ? tsToDate(dateOrTs) : (dateOrTs && new Date(dateOrTs)) || this.date;
    }

    /**
     * The instant's local wall-clock time. The Date already represents the
     * instant; local getters render it in the client's timezone — no manual
     * offset shifting (which would apply the offset twice).
     */
    toLocalTime = (format: string = "hh:ii"): string => dateToFormat(this.date, format);

    // Modify date =====================================================================================

    startOfDay = (): Ages => {
        this.date.setHours(0, 0, 0, 0);
        return this;
    };

    startOfWeek = (): Ages => {
        this.date = dateStartOfWeek(this.date);
        return this;
    };
    
    endOfWeek = (): Ages => {
        this.date = dateEndOfWeek(this.date);
        return this;
    };

    // Day/week arithmetic moves the calendar day and preserves the wall-clock
    // time (setDate adds; the previous setHours-based version *set* the hour
    // field, silently zeroing the time of day).

    addDays = (days= 1): Ages => {
        this.date.setDate(this.date.getDate() + days);
        return this;
    };

    addWeeks = (weeks= 1): Ages => this.addDays(7 * weeks);

    subDays = (days= 1): Ages => this.addDays(-days);

    subWeeks = (weeks= 1): Ages => this.addDays(-7 * weeks);

    // Comparison (never mutates either operand)
    equalsDay = (eqDate: Ages): boolean => {
        return this.copy().startOfDay().toTs() === eqDate.copy().startOfDay().toTs();
    };

    lt = (other: Ages): boolean => {
        return this.toTs() < other.toTs();
    };

    lte = (other: Ages): boolean => {
        return this.toTs() <= other.toTs();
    };

    gt = (other: Ages): boolean => {
        return this.toTs() > other.toTs();
    };

    gte = (other: Ages): boolean => {
        return this.toTs() >= other.toTs();
    };


    // Output & Formatting =============================================================================

    toFormat = (format: string = 'yyyy-mm-dd'): string => dateToFormat(this.date, format);

    toTs = (): number => dateToTs(this.date);

    toString = (): string => this.toFormat();

    copy = (): Ages => new Ages(new Date(this.date.getTime()));


}
